// WebSocket 端点字段（protocol==='WEBSOCKET' 时替换 method / HTTP 计费）。
//
// 从 register.jsx 的 EndpointEditor 抽出，收敛 wsConfig / wsBilling 的表单渲染，
// 与 register.jsx 里 HTTP 端点的 Field/Row/Panel/Select 风格对齐（但那些 helper 在
// register.jsx 是文件私有的，这里为避免耦合用等价的轻量本地版）。
//
// 数据形状（对齐后端 DTO）：
//   wsConfig: { adapter(必填), upstreamUrl(wss://,必填), clientPath?, subprotocols?: string[],
//               idleTimeoutSec?, maxDurationSec?, maxConnPerKey?, heartbeatSec?, maxFrameBytes? }
//   wsBilling: { kind: 'duration', perMinuteUsd(必填,数字), holdUsd? }
//
// adapter 下拉数据由挂载时 getWsAdapters() 拉取；按选中 adapter 的 requiredWsConfig
// 动态标必填（至少 upstreamUrl 恒为必填）。

import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { XApi } from '../../services.js';
import { XIcons } from '../../primitives.jsx';
import { Select } from '../../ui/index.js';

// ── 轻量本地 helper（与 register.jsx 的 Field/Row 同款视觉，独立以免跨文件私有引用）──
const WsField = ({ label, hint, required, error, children }) => (
  <div>
    <label className="field-label">
      {label} {required && <span style={{ color: 'var(--err)' }}>*</span>}
    </label>
    {children}
    {error?.message && <div style={{ marginTop: 5, color: 'var(--err)', fontSize: 11 }}>{error.message}</div>}
    {hint && !error?.message && (
      <div className="micro-mono" style={{ textTransform: 'none', letterSpacing: 0, marginTop: 5 }}>{hint}</div>
    )}
  </div>
);

const WsRow = ({ cols, children }) => (
  <div className="register-row" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols || 2}, 1fr)`, gap: 14, marginBottom: 14 }}>
    {children}
  </div>
);

// wss:// 基本格式校验（前端友好提示；权威校验仍在后端）。
export const isValidWsUrl = (v) => {
  if (!v || !String(v).trim()) return false;
  try {
    const u = new URL(String(v).trim());
    return u.protocol === 'ws:' || u.protocol === 'wss:';
  } catch {
    return false;
  }
};

// 数字字段的 onChange：空串 → undefined（不发送），否则 Number。immutable 交给 RHF。
const numOrUndef = (raw) => {
  const s = String(raw ?? '').trim();
  if (s === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

// 把数字渲染成**不带科学计数法**的普通十进制字符串（如 0.0000004 而非 4e-7）。
// token 模态单价常在 1e-6~4e-7 量级，String(n) 会转成指数式，输入框里显示 "4e-7" 很反直觉。
const noExp = (n) => {
  if (n == null || n === '') return '';
  const s = typeof n === 'number' ? n.toString() : String(n);
  if (!/e/i.test(s)) return s;
  const neg = s[0] === '-';
  const abs = neg ? s.slice(1) : s;
  const [mant, expPart] = abs.split(/e/i);
  const exp = parseInt(expPart, 10);
  const [intPart, fracPart = ''] = mant.split('.');
  const digits = intPart + fracPart;
  const pointPos = intPart.length + exp; // 小数点在 digits 中的新位置
  let out;
  if (pointPos <= 0) {
    out = '0.' + '0'.repeat(-pointPos) + digits;
  } else if (pointPos >= digits.length) {
    out = digits + '0'.repeat(pointPos - digits.length);
  } else {
    out = digits.slice(0, pointPos) + '.' + digits.slice(pointPos);
  }
  if (out.includes('.')) out = out.replace(/0+$/, '').replace(/\.$/, '');
  return (neg ? '-' : '') + out;
};

// 小数输入框：用本地文本缓冲，避免「立即转 Number」抹掉正在输入的小数点（"0." → 0 → "0"）。
// 显示恒为普通十进制（noExp），杜绝科学计数法；外部值变化（表单 reset / 切 kind）时才同步。
const DecimalInput = ({ field, placeholder }) => {
  const [text, setText] = React.useState(() => noExp(field.value));
  React.useEffect(() => {
    // 仅当外部值与当前文本解析结果不一致时才覆盖，防止把用户输入中的 "0." 抹成 "0"。
    if (numOrUndef(text) !== field.value) setText(noExp(field.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);
  return (
    <input
      className="input mono"
      type="text"
      inputMode="decimal"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        field.onChange(numOrUndef(e.target.value));
      }}
      onBlur={() => {
        field.onBlur?.();
        setText(noExp(field.value)); // 失焦规范化（去多余零/科学计数法）
      }}
      placeholder={placeholder}
    />
  );
};

// 复用的数字输入字段（模块级组件，避免在 render 内定义组件导致输入失焦）。
const WsNumField = ({ control, name, label, hint, required, placeholder, step = '0.0001', error }) => (
  <WsField label={label} hint={hint} required={required} error={error}>
    <Controller
      name={name}
      control={control}
      render={({ field }) => <DecimalInput field={field} placeholder={placeholder} />}
    />
  </WsField>
);

export const WsEndpointFields = ({ index }) => {
  const { register, control, watch, setValue, formState: { errors } } = useFormContext();
  const base = `endpoints.${index}.wsConfig`;
  const billingBase = `endpoints.${index}.wsBilling`;
  const epErr = errors.endpoints && errors.endpoints[index];
  const wsErr = epErr?.wsConfig || {};
  const billErr = epErr?.wsBilling || {};

  const [adapters, setAdapters] = React.useState([]);
  const [loadingAdapters, setLoadingAdapters] = React.useState(true);
  const [adapterError, setAdapterError] = React.useState(null);
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  const selectedAdapter = watch(`${base}.adapter`) || '';
  const upstreamUrl = watch(`${base}.upstreamUrl`) || '';
  const subprotocols = watch(`${base}.subprotocols`) || [];
  const billingKind = watch(`${billingBase}.kind`) || 'duration';

  // 挂载时拉 adapter 列表填充下拉。
  React.useEffect(() => {
    let cancelled = false;
    setLoadingAdapters(true);
    XApi.apiSvc.getWsAdapters()
      .then((list) => {
        if (cancelled) return;
        setAdapters(Array.isArray(list) ? list : []);
        setAdapterError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setAdapters([]);
        setAdapterError(e?.message || '加载 adapter 列表失败');
      })
      .finally(() => { if (!cancelled) setLoadingAdapters(false); });
    return () => { cancelled = true; };
  }, []);

  const currentAdapter = adapters.find((a) => a.name === selectedAdapter) || null;
  // 选中 adapter 的 requiredWsConfig（后端提供）→ 动态必填集合。upstreamUrl 恒必填。
  const requiredSet = React.useMemo(() => {
    const req = new Set(['upstreamUrl']);
    const list = currentAdapter && Array.isArray(currentAdapter.requiredWsConfig) ? currentAdapter.requiredWsConfig : [];
    for (const f of list) if (f) req.add(f);
    return req;
  }, [currentAdapter]);

  const adapterOptions = adapters.map((a) => ({ value: a.name, label: a.label || a.name }));
  const urlValid = !upstreamUrl || isValidWsUrl(upstreamUrl);

  // subprotocols 数组输入：逗号/换行/空格分隔 → 去空去重。immutable：setValue 传新数组。
  const subprotocolsText = Array.isArray(subprotocols) ? subprotocols.join(', ') : '';
  const onSubprotocolsChange = (raw) => {
    const arr = String(raw || '')
      .split(/[,\n\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    setValue(`${base}.subprotocols`, arr, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <div className="col gap-3">
      {/* ── adapter + 上游 ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 3, height: 13, borderRadius: 2, background: 'var(--c-mint, #5fd0a8)', flexShrink: 0, display: 'inline-block' }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>WebSocket 上游</span>
      </div>

      <WsField
        label="Adapter"
        required
        hint={
          adapterError
            ? adapterError
            : currentAdapter?.description
              ? currentAdapter.description
              : '决定客户端子协议与逐帧计量方式；由平台维护的适配器列表'
        }
        error={wsErr.adapter}
      >
        <Controller
          name={`${base}.adapter`}
          control={control}
          render={({ field }) => (
            <Select
              value={field.value || ''}
              onChange={field.onChange}
              disabled={loadingAdapters || adapters.length === 0}
              placeholder={loadingAdapters ? '加载中…' : adapters.length === 0 ? '无可用 adapter' : '选择 adapter…'}
              options={adapterOptions}
            />
          )}
        />
        {currentAdapter && (
          <div className="micro-mono" style={{ textTransform: 'none', letterSpacing: 0, marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {currentAdapter.clientProtocol && (
              <span style={{ padding: '1px 6px', borderRadius: 3, background: 'rgba(0,150,200,0.10)', color: 'rgb(0,90,140)' }}>
                客户端协议 · {currentAdapter.clientProtocol}
              </span>
            )}
            {currentAdapter.docUrl && (
              <a href={currentAdapter.docUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--c-mint, #5fd0a8)' }}>
                文档 ↗
              </a>
            )}
          </div>
        )}
      </WsField>

      <WsField
        label="上游 URL"
        required
        hint="wss:// 或 ws:// 开头的上游 WebSocket 地址"
        error={wsErr.upstreamUrl || (!urlValid ? { message: '必须是 wss:// 或 ws:// 开头的合法地址' } : undefined)}
      >
        <input
          className="input mono"
          placeholder="wss://upstream.example.com/realtime"
          {...register(`${base}.upstreamUrl`)}
        />
      </WsField>

      <WsField
        label="客户端路径"
        required={requiredSet.has('clientPath')}
        hint="调用方连接本网关时使用的路径（选填，缺省沿用 adapter 约定）"
        error={wsErr.clientPath}
      >
        <input
          className="input mono"
          placeholder="/v1/realtime"
          {...register(`${base}.clientPath`)}
        />
      </WsField>

      {/* ── 计费 ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        <span style={{ width: 3, height: 13, borderRadius: 2, background: 'var(--c-mint, #5fd0a8)', flexShrink: 0, display: 'inline-block' }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>计费</span>
      </div>
      <WsField label="计费口径" hint="duration=按连接时长；realtime-token=按 token 用量（如 OpenAI Realtime）" error={billErr.kind}>
        <Controller
          name={`${billingBase}.kind`}
          control={control}
          render={({ field }) => (
            <Select
              value={field.value || 'duration'}
              onChange={field.onChange}
              options={[
                { value: 'duration', label: '按连接时长 (duration)' },
                { value: 'realtime-token', label: '按 token 用量 (realtime-token)' },
              ]}
            />
          )}
        />
      </WsField>

      {billingKind === 'duration' ? (
        <WsRow cols={2}>
          <WsNumField control={control} name={`${billingBase}.perMinuteUsd`} label="每分钟单价 (USD)" required
            hint="按连接存活时长计费，$ / 分钟" placeholder="0.05" error={billErr.perMinuteUsd} />
          <WsNumField control={control} name={`${billingBase}.holdUsd`} label="预扣额度 (USD)"
            hint="建连时预扣的占位金额（选填），结算按实际时长多退少补" placeholder="1.00" error={billErr.holdUsd} />
        </WsRow>
      ) : (
        <>
          <WsRow cols={2}>
            <WsNumField control={control} name={`${billingBase}.holdUsd`} label="预扣额度 (USD)"
              hint="token 计费的预授权额度，也是在飞增量预扣起点。建议≈单会话预期成本（如 0.50）" placeholder="0.50" error={billErr.holdUsd} />
            <div />
          </WsRow>
          <div className="micro-mono" style={{ textTransform: 'none', letterSpacing: 0, marginBottom: 8 }}>
            模态分档价（推荐；Realtime 音频 token ≈ 文本 ~20×、cached 折扣）。留空的档回退到下方混合价。单位 USD/token。
          </div>
          <WsRow cols={2}>
            <WsNumField control={control} name={`${billingBase}.inputTextTokenUsd`} label="输入 · 文本" step="0.000001" placeholder="0.000004" error={billErr.inputTextTokenUsd} />
            <WsNumField control={control} name={`${billingBase}.outputTextTokenUsd`} label="输出 · 文本" step="0.000001" placeholder="0.000016" error={billErr.outputTextTokenUsd} />
          </WsRow>
          <WsRow cols={2}>
            <WsNumField control={control} name={`${billingBase}.inputAudioTokenUsd`} label="输入 · 音频" step="0.000001" placeholder="0.00004" error={billErr.inputAudioTokenUsd} />
            <WsNumField control={control} name={`${billingBase}.outputAudioTokenUsd`} label="输出 · 音频" step="0.000001" placeholder="0.00008" error={billErr.outputAudioTokenUsd} />
          </WsRow>
          <WsRow cols={2}>
            <WsNumField control={control} name={`${billingBase}.inputCachedTokenUsd`} label="输入 · 缓存 (cached)" step="0.000001" placeholder="0.0000004" error={billErr.inputCachedTokenUsd} />
            <div />
          </WsRow>
          <div className="micro-mono" style={{ textTransform: 'none', letterSpacing: 0, marginBottom: 8 }}>
            混合价（回退：未配模态价时，按 in/out 总量计）。
          </div>
          <WsRow cols={2}>
            <WsNumField control={control} name={`${billingBase}.inputTokenUsd`} label="混合 · 输入" step="0.000001" placeholder="0.000005" error={billErr.inputTokenUsd} />
            <WsNumField control={control} name={`${billingBase}.outputTokenUsd`} label="混合 · 输出" step="0.000001" placeholder="0.00002" error={billErr.outputTokenUsd} />
          </WsRow>
        </>
      )}

      {/* ── 高级（折叠）── */}
      <button
        type="button"
        className="btn btn-sm btn-ghost"
        onClick={() => setShowAdvanced((v) => !v)}
        style={{ alignSelf: 'flex-start', gap: 6 }}
      >
        <XIcons.IconChevronDown size={12} style={{ transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        高级选项（超时 / 心跳 / 并发 / 帧大小 / 子协议）
      </button>
      {showAdvanced && (
        <div className="col gap-3" style={{ padding: '4px 0 8px' }}>
          <WsRow cols={2}>
            <WsField label="空闲超时 (秒)" required={requiredSet.has('idleTimeoutSec')} hint="无数据往来多久后断开" error={wsErr.idleTimeoutSec}>
              <Controller
                name={`${base}.idleTimeoutSec`}
                control={control}
                render={({ field }) => (
                  <input className="input mono" type="number" min="1" value={field.value ?? ''} onChange={(e) => field.onChange(numOrUndef(e.target.value))} placeholder="60" />
                )}
              />
            </WsField>
            <WsField label="最大连接时长 (秒)" required={requiredSet.has('maxDurationSec')} hint="单次连接硬上限" error={wsErr.maxDurationSec}>
              <Controller
                name={`${base}.maxDurationSec`}
                control={control}
                render={({ field }) => (
                  <input className="input mono" type="number" min="1" value={field.value ?? ''} onChange={(e) => field.onChange(numOrUndef(e.target.value))} placeholder="3600" />
                )}
              />
            </WsField>
          </WsRow>
          <WsRow cols={2}>
            <WsField label="每 key 最大并发连接" required={requiredSet.has('maxConnPerKey')} hint="同一 API key 的并发连接上限" error={wsErr.maxConnPerKey}>
              <Controller
                name={`${base}.maxConnPerKey`}
                control={control}
                render={({ field }) => (
                  <input className="input mono" type="number" min="1" value={field.value ?? ''} onChange={(e) => field.onChange(numOrUndef(e.target.value))} placeholder="5" />
                )}
              />
            </WsField>
            <WsField label="心跳间隔 (秒)" required={requiredSet.has('heartbeatSec')} hint="ping/pong 保活间隔" error={wsErr.heartbeatSec}>
              <Controller
                name={`${base}.heartbeatSec`}
                control={control}
                render={({ field }) => (
                  <input className="input mono" type="number" min="1" value={field.value ?? ''} onChange={(e) => field.onChange(numOrUndef(e.target.value))} placeholder="30" />
                )}
              />
            </WsField>
          </WsRow>
          <WsField label="最大帧字节数" required={requiredSet.has('maxFrameBytes')} hint="单帧最大字节，超出即拒" error={wsErr.maxFrameBytes}>
            <Controller
              name={`${base}.maxFrameBytes`}
              control={control}
              render={({ field }) => (
                <input className="input mono" type="number" min="1" value={field.value ?? ''} onChange={(e) => field.onChange(numOrUndef(e.target.value))} placeholder="1048576" />
              )}
            />
          </WsField>
          <WsField label="子协议 (subprotocols)" required={requiredSet.has('subprotocols')} hint="逗号或空格分隔，多个 WebSocket 子协议名" error={wsErr.subprotocols}>
            <input
              className="input mono"
              placeholder="openai-beta.realtime-v1, json"
              defaultValue={subprotocolsText}
              onBlur={(e) => onSubprotocolsChange(e.target.value)}
            />
          </WsField>
        </div>
      )}
    </div>
  );
};
